/**
 * M2 P3: NPC traffic meshes + floating-origin placement. Mirrors StationRenderer:
 * each NPC's authoritative position is Float64 (sim-core npc.ts); the render
 * position is the Float32 delta from the ship's world pos. Reuses the ship GLBs
 * (normaliseToRadius) with a cone fallback so missing assets never block.
 */
import * as THREE from 'three';
import { assetDir } from './assetDir.js';
import type { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { createGltfLoader } from './gltf.js';
import { type Vec3, type NpcShip } from '@blackmarble/sim-core';
import { normaliseToRadius, enableShadows } from './bodyFactory.js';

const NPC_DISPLAY_KM = 5; // render half-extent (sim units = km)
const FLASH_SEC = 0.12;
const FLASH_COLOR = new THREE.Color(0xff8e5a);

type FlashMaterial = THREE.MeshStandardMaterial | THREE.MeshPhysicalMaterial;

/**
 * Ship hulls read flat under the scene's dim Sol-only lighting. Boost their IBL
 * contribution above the gentle global scene.environmentIntensity so weathered
 * paint, edge wear, and panel detail register in shadow — kept matte (no glow).
 */
const SHIP_ENV_INTENSITY = 2.0;
const tuneShipMaterials = (root: THREE.Object3D): void => {
  root.traverse((o) => {
    if (!(o instanceof THREE.Mesh)) return;
    const mats = Array.isArray(o.material) ? o.material : [o.material];
    for (const mat of mats) {
      if ('envMapIntensity' in mat) {
        (mat as THREE.MeshStandardMaterial).envMapIntensity = SHIP_ENV_INTENSITY;
        mat.needsUpdate = true;
      }
    }
  });
};

const flashMaterials = (group: THREE.Group, amount: number): void => {
  group.traverse((o) => {
    if (!(o instanceof THREE.Mesh)) return;
    const mats = Array.isArray(o.material) ? o.material : [o.material];
    for (const mat of mats) {
      if (!('emissive' in mat) || !('emissiveIntensity' in mat)) continue;
      const flashMat = mat as FlashMaterial;
      if (!(flashMat.userData.baseEmissive instanceof THREE.Color)) {
        flashMat.userData.baseEmissive = flashMat.emissive.clone();
        flashMat.userData.baseEmissiveIntensity = flashMat.emissiveIntensity;
      }
      const base = flashMat.userData.baseEmissive as THREE.Color;
      const baseIntensity = flashMat.userData.baseEmissiveIntensity as number;
      flashMat.emissive.copy(base).lerp(FLASH_COLOR, amount * 0.8);
      flashMat.emissiveIntensity = baseIntensity + amount * 1.2;
    }
  });
};

export class NpcRenderer {
  private readonly groups = new Map<string, THREE.Group>();
  private readonly flashTimers = new Map<string, number>();

  constructor(
    private readonly npcs: readonly NpcShip[],
    private readonly parent: THREE.Group,
  ) {}

  async loadAll(baseUrl = assetDir('ships')): Promise<void> {
    const loader = createGltfLoader();
    for (const npc of this.npcs) {
      const group = new THREE.Group();
      group.name = `npc:${npc.id}`;
      await this.loadOne(loader, group, npc, baseUrl);
      this.parent.add(group);
      this.groups.set(npc.id, group);
    }
  }

  private async loadOne(
    loader: GLTFLoader,
    group: THREE.Group,
    npc: NpcShip,
    baseUrl: string,
  ): Promise<void> {
    try {
      const gltf = await loader.loadAsync(`${baseUrl}${npc.shipType}.glb`);
      normaliseToRadius(gltf.scene, NPC_DISPLAY_KM);
      tuneShipMaterials(gltf.scene);
      enableShadows(gltf.scene); // WAVE 2a: NPC hulls self-shadow + cast on neighbours
      group.add(gltf.scene);
    } catch {
      const geo = new THREE.ConeGeometry(NPC_DISPLAY_KM * 0.5, NPC_DISPLAY_KM * 2, 8);
      const mat = new THREE.MeshStandardMaterial({ color: 0xffcc66, roughness: 0.5, metalness: 0.4 });
      const cone = new THREE.Mesh(geo, mat);
      cone.castShadow = true;
      cone.receiveShadow = true;
      group.add(cone);
    }
  }

  /**
   * M3 WORLD B2: add a single NPC mesh after spawn (encounter enc-* ships injected
   * on a signal drop-in). Reuses the same GLB-with-cone-fallback path as loadAll.
   * Idempotent: a no-op if the id already has a mesh.
   */
  async add(npc: NpcShip, baseUrl = assetDir('ships')): Promise<void> {
    if (this.groups.has(npc.id)) return;
    const loader = createGltfLoader();
    const group = new THREE.Group();
    group.name = `npc:${npc.id}`;
    await this.loadOne(loader, group, npc, baseUrl);
    this.parent.add(group);
    this.groups.set(npc.id, group);
  }

  /** P4: remove a destroyed NPC's mesh from the scene. */
  remove(id: string): void {
    const g = this.groups.get(id);
    if (!g) return;
    g.removeFromParent();
    this.groups.delete(id);
    this.flashTimers.delete(id);
  }

  /** Floating-origin placement + orient along velocity. Same delta math as bodies. */
  update(npcs: readonly NpcShip[], shipWorldPos: Vec3, dt: number): void {
    for (const npc of npcs) {
      const g = this.groups.get(npc.id);
      if (!g) continue;
      g.position.set(npc.pos.x - shipWorldPos.x, npc.pos.y - shipWorldPos.y, npc.pos.z - shipWorldPos.z);
      const v = npc.vel;
      if (v.x || v.y || v.z) g.lookAt(g.position.x + v.x, g.position.y + v.y, g.position.z + v.z);
      const next = Math.max(0, (this.flashTimers.get(npc.id) ?? 0) - dt);
      flashMaterials(g, next / FLASH_SEC);
      if (next > 0) this.flashTimers.set(npc.id, next);
      else this.flashTimers.delete(npc.id);
    }
  }

  flash(id: string): void {
    if (!this.groups.has(id)) return;
    this.flashTimers.set(id, FLASH_SEC);
  }
}
